// Copyright (c) Docugami, Inc. All rights reserved.

import { InteractionStates } from "networking/NotificationsApi";
import { NotificationsManagerSingleton } from "notifications/NotificationsManager";
import { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { AppState } from "store";
import { RequestState, useAsyncHelper } from "./AsyncHelper";

/**
 * Hook for marking notifications created by the service as read. It properly handles queueing multiple requests
 * as well as removing duplicate IDs and those for notifications that have already been read.
 */
export const useServiceNotificationMarkAsRead = () => {
    const [pending, setPending] = useState<string[]>([]);
    const orgId = useSelector((state: AppState) => state.organizations.currentOrganization?.id);

    const { execute, state } = useAsyncHelper("Mark service notifications as read");

    const queueMarkAsRead = useCallback((ids: string[], forceImmediate: boolean = false) => {
        ids = ids.filter(id => {
            const notification = NotificationsManagerSingleton.getNotification(id);
            return !!notification && !(notification.interactions & InteractionStates.Read);
        });

        if (forceImmediate) {
            NotificationsManagerSingleton.markAsRead(orgId, [...ids])
        }
        else {
            setPending(x => {
                ids = ids.filter(id => !x.includes(id));
                return [...x, ...ids]
            });
        }
    }, [setPending, orgId]);

    useEffect(() => {
        if (state === RequestState.InProgress || !orgId) {
            return;
        }

        if (pending.length > 0) {
            execute(NotificationsManagerSingleton.markAsRead(orgId, [...pending]));
            setPending([]);
        }
    }, [pending, state, orgId, execute])

    return queueMarkAsRead;
}
